// =============================================================
//  UserDetailScreen — Détail d'un utilisateur, centre ou admin
//  Bloquer/débloquer, statistiques, infos.
// =============================================================

import React, { useState } from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { doc, updateDoc, addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { db } from '../private/firebaseConfig';

import AdminLayout from './web-ui/AdminLayout';
import { Card, Button, Badge, Avatar, Divider } from './web-ui/kit';
import { colors, spacing } from './web-ui/theme';

const fmtDate = (ts) => {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString('fr-FR');
};

export default function UserDetailScreen({ route, navigation }) {
  const initialUser = route?.params?.user || {};
  const userType = route?.params?.userType || 'users';
  const [user, setUser] = useState(initialUser);
  const [loading, setLoading] = useState(false);

  const isCenter = userType === 'centers';
  const collectionName = isCenter ? 'centers' : userType === 'admins' ? 'admin' : 'users';
  const isBlocked = user.status === 'blocked';

  const toggleBlock = async () => {
    setLoading(true);
    try {
      const newStatus = isBlocked ? 'active' : 'blocked';
      await updateDoc(doc(db, collectionName, user.id), {
        status: newStatus,
        statusUpdatedAt: serverTimestamp(),
      });
      try {
        await addDoc(collection(db, 'notifications'), {
          userId: user.id,
          type: newStatus === 'blocked' ? 'account_blocked' : 'account_unblocked',
          createdAt: serverTimestamp(),
        });
      } catch {}
      setUser((p) => ({ ...p, status: newStatus }));
    } catch (e) {
      console.log('toggle block error', e?.message || e);
    } finally {
      setLoading(false);
    }
  };

  const name = user.fullName || user.centerName || user.name || 'Sans nom';

  return (
    <AdminLayout
      title={name}
      subtitle={isCenter ? 'Détail du centre' : userType === 'admins' ? 'Détail administrateur' : 'Détail utilisateur'}
    >
      <View style={{ flexDirection: 'row', justifyContent: 'flex-end', marginBottom: spacing.md, gap: spacing.sm }}>
        <Button variant="secondary" icon="arrow-left" onPress={() => navigation.goBack()}>
          Retour
        </Button>
        <Button
          variant={isBlocked ? 'primary' : 'danger'}
          icon={isBlocked ? 'lock-open-outline' : 'lock-outline'}
          loading={loading}
          onPress={toggleBlock}
        >
          {isBlocked ? 'Débloquer' : 'Bloquer'}
        </Button>
      </View>

      <Card>
        <View style={{ flexDirection: 'row', gap: spacing.lg, alignItems: 'center' }}>
          <Avatar src={user.photoURL || user.imageUrl} name={name} size={72} />
          <View style={{ flex: 1, minWidth: 0 }}>
            <Text style={styles.title}>{name}</Text>
            <Text style={styles.subtitle}>{user.email || '—'}</Text>
            <View style={{ flexDirection: 'row', gap: 6, marginTop: 8 }}>
              <Badge variant={isBlocked ? 'danger' : 'success'}>
                {isBlocked ? 'Bloqué' : 'Actif'}
              </Badge>
              <Badge variant="primary">{userType}</Badge>
              {user.adminLevel && <Badge variant="info">{user.adminLevel}</Badge>}
            </View>
          </View>
        </View>

        <Divider />

        <View style={styles.grid}>
          <InfoItem label="Email" value={user.email} icon="email-outline" />
          <InfoItem label="Téléphone" value={user.phoneNumber} icon="phone-outline" />
          <InfoItem label="Âge" value={user.age} icon="cake-variant-outline" />
          <InfoItem label="Genre" value={user.gender} icon="account-outline" />
          <InfoItem label="Région" value={user.region} icon="map-marker-outline" />
          <InfoItem label="Inscription" value={fmtDate(user.createdAt)} icon="calendar-outline" />
        </View>
      </Card>
    </AdminLayout>
  );
}

function InfoItem({ label, value, icon }) {
  return (
    <View style={styles.infoItem}>
      <Text style={styles.infoLabel}>{label}</Text>
      <Text style={styles.infoValue} numberOfLines={1}>{value || '—'}</Text>
    </View>
  );
}

const styles = StyleSheet.create({
  title: { fontSize: 20, fontWeight: '700', color: colors.text },
  subtitle: { fontSize: 13, color: colors.textSecondary, marginTop: 4 },
  grid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.md,
  },
  infoItem: {
    flex: 1,
    minWidth: 200,
    backgroundColor: colors.surfaceAlt,
    padding: spacing.md,
    borderRadius: 10,
  },
  infoLabel: { fontSize: 11, color: colors.textMuted, fontWeight: '600', textTransform: 'uppercase', letterSpacing: 0.5 },
  infoValue: { fontSize: 14, color: colors.text, fontWeight: '600', marginTop: 4 },
});
